import {Component, Inject, OnInit} from "@angular/core";
import {MessageService} from "../../../../core/services/message-service";
import {NotificationService} from "../../../../core/services/notification.service";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {LocationsControllerService} from "../../../../core/api/controllers/locations-controller.service";
import {ValidationHelperService} from "../../../../core/helpers/validation-helper.service";
import {GeographyControllerService} from "../../../../core/api/controllers/geography-controller.service";
import {Country, IGeographicalEntity} from "../../../../core/models/geography.model";
import {LocationFormFactory} from "./LocationFormFactory";
import {startWith} from "rxjs/operators";
import {map} from "rxjs/internal/operators";
import {EAction} from "../../../../shared/enums/EAction";
import {ILocation} from "../../../../shared/interfaces/ILocation";

export interface ILocationActionDialogData {
  action: EAction;
  location?: ILocation;
}

@Component({
  selector: "app-location-action",
  templateUrl: "./location-action.component.html",
  styleUrls: ["./location-action.component.scss"],
})
export class LocationActionComponent implements OnInit {
  locationForm = LocationFormFactory.create();
  locations = [];
  user: any;
  action: EAction;
  location: ILocation = null;
  allreadyPressed = false;
  isLoading = false;
  isLoadingStates = false;
  countries: Country[] = [];
  filteredCountries: Country[] = [];

  states: IGeographicalEntity[] = [];
  filteredStates: IGeographicalEntity[] = [];

  constructor(
    private messageService: MessageService,
    private locationsController: LocationsControllerService,
    private notificationService: NotificationService,
    private dialogRef: MatDialogRef<LocationActionComponent>,
    private geographyController: GeographyControllerService,
    @Inject(MAT_DIALOG_DATA) private data: ILocationActionDialogData,
  ) {}

  ngOnInit() {
    this.action = this.data.action;
    if(this.action === EAction.Edit) this.prefillForm();
    this.getCountries();
  }

  prefillForm(): void {
    this.location = this.data.location;
    if (this?.location?.name) {
      this.locationForm.controls.name.setValue(this.location.name);
    }
  }


  getCountries(): void {
    this.isLoading = true;
    this.geographyController.getAllCountries().subscribe({
      next: res => {
        this.countries = res;
        this.filteredCountries = this.countries;
        if (this?.location?.country) {
          this.locationForm.controls.country.setValue(this.countries.find(state => state.id === this.location.country.id));
          this.onCountrySelected();
        }
        this.subscribeToCountryInput();
        this.subscribeToStateInput();
        this.isLoading = false;
      },
      error: err => {
        this.notificationService.error(err);
        this.isLoading = false;
      }
    })
  }

  subscribeToCountryInput(): void {
    this.locationForm.controls.country.valueChanges.pipe(
        startWith(''),
        map(value => this.filterCountries(value || '')),
    ).subscribe(res => {
      this.filteredCountries = res;
    });
  }

  subscribeToStateInput(): void {
    this.locationForm.controls.state.valueChanges.pipe(
        startWith(''),
        map(value => this.filterStates(value || '')),
    ).subscribe(res => {
      this.filteredStates = res;
    });
  }


  private filterStates(value: IGeographicalEntity | string): IGeographicalEntity[] {
    if(typeof value !== "string") return;
    const filterValue = (value as string).toLowerCase();
    return this.states.filter(option => option.name.toLowerCase().includes(filterValue));
  }

  private filterCountries(value: Country | string): Country[] {
    if(typeof value !== "string") return;
    const filterValue = (value as string).toLowerCase();
    return this.countries.filter(option => option.name.toLowerCase().includes(filterValue));
  }

  displayCountry(value: Country): string {
    return value?.name ?? '';
  }

  displayState(value: IGeographicalEntity): string {
    return value?.name ?? '';
  }

  onCountrySelected(): void {
    this.isLoadingStates = true;
    this.geographyController.getStatesByCountry((this.locationForm.controls.country.value as Country).id).subscribe({
      next: (res) => {
        this.states = res;
        this.filteredStates = res;
        if (this?.location?.state) this.locationForm.controls.state.setValue(this.filteredStates.find(state => state.id === this.location.state.id));
        else this.locationForm.controls.state.setValue('');
        this.isLoadingStates = false;
      },
      error: (err) => {
        this.notificationService.error(err);
        this.isLoadingStates = false;
      }
    })
  }

  onSubmit() {
    this.locationForm.markAllAsTouched();
    if(this.locationForm.invalid) return;
    if (this.action === EAction.Add) this.createLocation();
    else  this.editLocation();
  }

  editLocation(): void {
    this.allreadyPressed = true;
    this.isLoading = true;
    this.locationsController
        .editLocation(this.location.id, this.createPayload())
        .subscribe(
            () => {
              this.notificationService.success("Location added");
              this.dialogRef.close(true);
              this.isLoading = false;
            },
            (error: {error: string}) => {
              this.notificationService.error(error);
              this.dialogRef.close(true);
              this.isLoading = false;
            },
        );
  }

  createLocation(): void {
    this.allreadyPressed = true;
    this.isLoading = true;
    this.locationsController
        .addLocation(this.createPayload())
        .subscribe(
            () => {
              this.notificationService.success(
                  "The new Location will be added in a few moments",
              );
              this.dialogRef.close(true);
              this.messageService.accessMessage().subscribe((data) => {
                if (!data) {
                  this.messageService.sendMessage(true);
                }
              });
              this.isLoading = false;
            },
            (error: {error: string}) => {
              this.notificationService.error(error);
              this.dialogRef.close(true);
              this.isLoading = false;
            },
        );
  }

  createPayload(): any {
    return {
      name: this.locationForm.controls.name.value,
      imageUrl: "",
      stateId: (this.locationForm.controls.state.value as IGeographicalEntity)?.id ?? null,
      countryId: (this.locationForm.controls.country.value as IGeographicalEntity)?.id ?? null
    };
  }

    protected readonly ValidationHelperService = ValidationHelperService;
}
